import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {BookOfShadowsGameConfig} from "../BookOfShadowsGameConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {ScalableLayer} from "@nolimitcity/slot-game/bin/core/stage/ScalableLayer";
import {TimelineSprite} from "@nolimitcity/slot-game/bin/core/animation/TimelineSprite";
import {Helper} from "@nolimitcity/slot-game/bin/core/utils/Helper";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {SpinStart} from "../gameround/spin/SpinStart";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {StateReelSymbol} from "@nolimitcity/slot-game/bin/core/reelsymbol/StateReelSymbol";
import {BookOfShadowsReelController} from "../reel/BookOfShadowsReelController";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {BookOfShadowsGameMode} from "../gameMode/BookOfShadowsGameMode";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";

export class BookOfShadowsReelBackgroundView extends BaseView {
    private _reelsBg: PIXI.Sprite;
    private _reelsFrame: PIXI.Sprite;
    private _dividerAnimItems: TimelineSprite[] = [];
    private _logo: PIXI.Sprite;
    private _logoFire: TimelineSprite;
    private _mode: string = "";
    private _nextMode: string = "";

    /**
     * @description To reset to normal state all the visible symbols of reel area when gamble is finished.
     */
    public resetAllSymsToNormal(): void {
        const gameConfig: BookOfShadowsGameConfig = BookOfShadowsGameConfig.instance,
            len: number = gameConfig.SYMBOLS_NUM_IN_REEL[2];

        let symbol: StateReelSymbol;

        for (let i = 0; i < gameConfig.REELS_NUM; i++) {
            for (let j = 0; j < len; j++) {
                symbol = Reels.getSymbol(i, j)!;

                symbol.changeState({
                    state: gameConfig.SYMBOL_STATES.normal,
                    fadeInDuration: 0.1
                });
            }

            // to maintain the depth of the symbols as per this game.
            (Reels.getReel(i)! as BookOfShadowsReelController)!.reOrderSymbols();
        }

        const layer: ScalableLayer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.S_GLOW_RUNE.name);

        for (let i = 0; i < layer.children.length; i++) {
            const container: PIXI.Container = (layer.children[i] as PIXI.Container);
            for (let j = 0; j < container.children.length; j++) {
                (container.children[j] as PIXI.Sprite).tint = 0xffffff;
            }
        }
    }

    /**
     * @description Handle free spin mode.
     * @param fs
     */
    public toggleFsMode(fs: boolean) {
        if (fs) {
            this._logoFire.playLoop();
            this._logoFire.show();
        } else {
            this._logoFire.stopLoop();
            this._logoFire.hide();
        }
    }

    /**
     * @inheritDoc
     */
    protected initAnimations(): void {
        const gameConfig: BookOfShadowsGameConfig = BookOfShadowsGameConfig.instance,
            reelsFrameYPos: number = 292,
            reelsFrameLayer: ScalableLayer = StageManager.getLayer(gameConfig.LAYERS.REELS_FRAME.name);

        this._reelsBg = new PIXI.Sprite(GameResources.getTextures("reels")[0]);
        this._reelsBg.anchor.set(0.5);
        this._reelsBg.position.set(gameConfig.REEL_AREA_WIDTH / 2, gameConfig.GAME_HEIGHT / 2);
        StageManager.getLayer(gameConfig.LAYERS.REEL_BACKGROUND.name).addChild(this._reelsBg);

        this.createReelDividerAnimation(reelsFrameLayer);

        this._reelsFrame = new PIXI.Sprite(GameResources.getTextures("reelsFrame")[0]);
        this._reelsFrame.anchor.set(0.5);
        this._reelsFrame.position.set(gameConfig.REEL_AREA_WIDTH / 2, reelsFrameYPos);
        reelsFrameLayer.addChild(this._reelsFrame);

        this.createLogo();
        if (this._mode === BookOfShadowsGameMode.FREESPIN && this._nextMode === BookOfShadowsGameMode.FREESPIN) {
            this.toggleFsMode(true);
        }
    }

    /**
     * @inheritDoc
     */
    protected addEventListeners(): void {
        super.addEventListeners && super.addEventListeners();
        EventHandler.addEventListener(this, SpinStart.SPIN_STARTED_EVENT, () => this.playDividerAnimation());
        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0].mode, event.params[0].nextMode));
    }

    /**
     * @inheritDoc
     * @param resizeData
     */
    protected onResize(resizeData: IResizeData) {
        super.onResize && super.onResize(resizeData);

        if (NolimitApplication.isLandscape) {
            this._logo.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, 17);
            this._logoFire.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, 4);
        } else {
            this._logo.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, 639);
            this._logoFire.position.set(BookOfShadowsGameConfig.instance.REEL_AREA_WIDTH / 2, 631);
        }
    }

    /**
     * @description To create the reel divider animation.
     * @param layer
     */
    private createReelDividerAnimation(layer: ScalableLayer): void {
        const len: number = 4,
            symWidth: number = BookOfShadowsGameConfig.instance.SYMBOL_WIDTH;

        let item: TimelineSprite,
            pos: number[],
            yOffset: number = 28;

        for (let i = 0; i < len; i++) {
            pos = Helper.getSymbolPositions(i + 1, 2);

            item = new TimelineSprite(GameResources.getTextures("dividerAnim"));
            item.visible = false;
            item.anchor.set(0.5);
            item.position.set(pos[0] - symWidth / 2, pos[1] - yOffset);
            if (i % 2 !== 0) {
                item.scale.x *= -1;
            }

            this._dividerAnimItems.push(item);
            layer.addChild(item);
        }
    }

    /**
     * @description To create the logo and its fire.
     */
    private createLogo(): void {
        const layer: ScalableLayer = StageManager.getLayer(BookOfShadowsGameConfig.instance.LAYERS.LOGO.name);

        let logo: PIXI.Sprite,
            logoFire: TimelineSprite;

        logo = new PIXI.Sprite(GameResources.getTextures("FrameLogo")[0]);
        logo.anchor.set(0.5, 0);

        logoFire = new TimelineSprite(GameResources.getTextures("logoFire"));
        logoFire.hide();
        logoFire.anchor.set(0.5, 0);
        logoFire.blendMode = PIXI.BLEND_MODES.ADD;

        layer.addChild(logo);
        layer.addChild(logoFire);

        this._logo = logo;
        this._logoFire = logoFire;
    }

    /**
     * @description To play the reel divider animation whenever the spin starts.
     */
    private playDividerAnimation(): void {
        this._dividerAnimItems.forEach((divider: TimelineSprite) => {
            divider.getAnimationAutoShowHide(true, true);
        });
    }

    /**
     * @description Init data handler.
     * @param mode
     * @param nextMode
     */
    private onInitDataParsed(mode: string, nextMode: string): void {
        if (mode === BookOfShadowsGameMode.FREESPIN && nextMode === BookOfShadowsGameMode.FREESPIN && this._logoFire) {
            this.toggleFsMode(true);
        }
        this._mode = mode;
        this._nextMode = nextMode;

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }
}